import { API, getRequest, postRequest } from '@/api/api';

import type {
  PlaceDto,
  PlacePredictionDto,
  PlacesAutocompleteRequestDto,
  PlacesAutocompleteResponseDto,
  PlacesGeocodeResponseDto,
  PlacesNearbyRequestDto,
  PlacesNearbyResponseDto,
} from './places.dto';
import { mapGeocode, mapPredictions, type GeocodeResult } from './places.mappers';

/** Google Places and Geocoding through service-provider: the Maps key stays on the server. */
const PLACES_URL = '/service-provider/places';

export const autocompletePlaces = async (
  body: PlacesAutocompleteRequestDto,
  signal?: AbortSignal,
): Promise<PlacePredictionDto[]> => {
  const { data } = await postRequest<PlacesAutocompleteResponseDto, PlacesAutocompleteRequestDto>(
    API.SERVICE_PROVIDERS,
    `${PLACES_URL}/autocomplete`,
    body,
    false,
    undefined,
    { signal },
  );
  return mapPredictions(data);
};

export const getPlace = async (placeId: string): Promise<PlaceDto> => {
  const { data } = await getRequest<PlaceDto>(
    API.SERVICE_PROVIDERS,
    `${PLACES_URL}/${encodeURIComponent(placeId)}`,
  );
  return data;
};

export const searchNearbyPlaces = async (body: PlacesNearbyRequestDto): Promise<PlaceDto[]> => {
  const { data } = await postRequest<PlacesNearbyResponseDto, PlacesNearbyRequestDto>(
    API.SERVICE_PROVIDERS,
    `${PLACES_URL}/nearby`,
    body,
  );
  return data?.places ?? [];
};

/** Rejects for every Geocoding status but OK and ZERO_RESULTS (REQUEST_DENIED, quota, …). */
export const geocodeAddress = async (address: string): Promise<GeocodeResult> => {
  const { data } = await getRequest<PlacesGeocodeResponseDto>(
    API.SERVICE_PROVIDERS,
    `${PLACES_URL}/geocode`,
    false,
    { address },
  );
  return mapGeocode(data);
};

// ── The address pickers' autocomplete → details pair ─────────────────────────

export interface AutocompletePrediction {
  placeId: string;
  description: string;
}

/** Google Places autocomplete filters. An empty region list is left out. */
export interface PredictionOptions {
  includedPrimaryTypes?: string[];
  includedRegionCodes?: string[];
}

/** Suggestions for a picker; none below `minLength` characters, and none (logged) on failure. */
export const fetchAutocompletePredictions = async (
  input: string,
  options?: PredictionOptions,
  minLength = 3,
): Promise<AutocompletePrediction[]> => {
  if (!input?.trim() || input.length < minLength) return [];

  try {
    const predictions = await autocompletePlaces({
      input,
      ...(options?.includedPrimaryTypes
        ? { includedPrimaryTypes: options.includedPrimaryTypes }
        : {}),
      ...(options?.includedRegionCodes?.length
        ? { includedRegionCodes: options.includedRegionCodes }
        : {}),
    });
    return predictions.map(({ placeId, text }) => ({ placeId, description: text?.text ?? '' }));
  } catch (error) {
    console.error('Error fetching autocomplete predictions:', error);
    return [];
  }
};

/** A picked suggestion's details, or null (logged) on failure. */
export const fetchPlaceDetails = async (
  placeId: string,
): Promise<(PlaceDto & { id: string }) | null> => {
  try {
    const place = await getPlace(placeId);
    return { ...place, id: place.id ?? placeId };
  } catch (error) {
    console.error('Error fetching place details:', error);
    return null;
  }
};
